import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface CallbackProps {
  onLogin: (token: string) => void;
}

const Callback: React.FC<CallbackProps> = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.log('Callback timeout reached');
      setError('Authentication timeout. Please try again.');
      setIsLoading(false);
    }, 10000); // 10 second timeout

    const handleCallback = async () => {
      try {
        console.log('Callback handleCallback started');
        console.log('Current URL:', window.location.href);
        console.log('Search params:', window.location.search);
        
        // Check if we got redirected here from the backend with a token
  const token = searchParams.get('token');
  const error = searchParams.get('error');
  const googleLogin = searchParams.get('google_login');
  const linkedinLogin = searchParams.get('linkedin_login');
  const googleAccessToken = searchParams.get('google_access_token');
        
        console.log('Callback params:', { 
          token: token ? 'present' : 'missing', 
          error, 
          googleLogin, 
          linkedinLogin 
        });
        
        if (error) {
          console.log('Error found in params:', error);
          setError(`Authentication error: ${error.replace(/_/g, ' ')}`);
          setIsLoading(false);
          clearTimeout(timeout);
          return;
        }

        if (token && (googleLogin === 'success' || linkedinLogin === 'success')) {
          console.log('Valid token and login success found, processing...');
          // Store the token and redirect to dashboard
          localStorage.setItem('authToken', token);
          if (googleAccessToken) {
            localStorage.setItem('googleAccessToken', googleAccessToken);
          }
          onLogin(token);
          setIsLoading(false);
          
          console.log('Token stored and onLogin called, navigating to dashboard...');
          // Navigate to dashboard
          navigate('/dashboard', { replace: true });
          clearTimeout(timeout);
          return;
        }

        // If we don't have a token but have auth codes, something went wrong
        const code = searchParams.get('code');
        console.log('No valid token/success combination found');
        console.log('Available params:', Object.fromEntries(searchParams.entries()));
        
        if (code) {
          setError('Authentication completed but token not received. Please try again.');
        } else {
          setError('Invalid authentication response. Please try again.');
        }
        
        setIsLoading(false);
        clearTimeout(timeout);
      } catch (err) {
        console.error('Callback error:', err);
        setError('Authentication failed. Please try again.');
        setIsLoading(false);
      }
    };

    handleCallback();
  }, []); // Empty dependency array to prevent infinite loops

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Completing authentication...
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please wait while we verify your account.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-100 dark:bg-red-900 rounded-full h-12 w-12 mx-auto mb-4 flex items-center justify-center">
            <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Authentication Failed
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default Callback;
